import React from 'react';
import ReactDOM from 'react-dom';

/**
 * 弹窗组件必须支持的属性接口
 */
export interface ModalComponentProps {
  visible?: boolean;
  open?: boolean;
  onOk?: (result?: string) => void;
  onCancel?: () => void;
  [key: string]: unknown;
}

/**
 * 模态框事件的类型定义
 */
interface ModalOpenEvent {
  type: 'OPEN_MODAL';
  id: string;
  containerName: string;
  component: React.ComponentType<ModalComponentProps>;
  props: Record<string, unknown>;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

interface ModalCloseAllEvent {
  type: 'CLOSE_ALL';
  containerName?: string;
}

type ModalEvent = ModalOpenEvent | ModalCloseAllEvent;

/**
 * 模态框事件总线
 */
class ModalEventBus {
  private listeners: ((data: ModalEvent) => void)[] = [];

  /**
   * 添加监听器
   */
  subscribe(listener: (data: ModalEvent) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * 发送消息
   */
  publish(data: ModalEvent): void {
    this.listeners.forEach(listener => listener(data));
  }
}

// 创建事件总线实例
const modalEventBus = new ModalEventBus();

/**
 * 打开模态框的选项
 */
interface OpenModalOptions {
  containerName?: string; // 目标容器名称，默认为 'default'
}

/**
 * 模态框服务 - 提供命令式API
 */
export const ModalService = {
  /**
   * 打开模态框
   * @param ModalComponent 模态框组件
   * @param props 组件属性
   * @param options 打开选项，可指定目标容器
   * @returns Promise，resolve时返回结果，reject时表示取消
   */
  open<T = unknown, P extends ModalComponentProps = ModalComponentProps>(
    ModalComponent: React.ComponentType<P>,
    props: Omit<P, 'visible' | 'open' | 'onOk'> = {} as Omit<P, 'visible' | 'open' | 'onOk'>,
    options: OpenModalOptions = {}
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      // 生成唯一ID
      const id = `modal-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

      // 获取目标容器名称，默认为 'default'
      const containerName = options.containerName || 'default';

      // 发布打开模态框的消息
      modalEventBus.publish({
        type: 'OPEN_MODAL',
        id,
        containerName, // 新增：指定目标容器
        component: ModalComponent as React.ComponentType<ModalComponentProps>,
        props: props as Record<string, unknown>,
        resolve: resolve as (value: unknown) => void,
        reject,
      });
    });
  },

  /**
   * 关闭所有模态框
   * @param containerName 可选，指定关闭哪个容器的弹窗，不传则关闭所有容器
   */
  closeAll(containerName?: string): void {
    modalEventBus.publish({
      type: 'CLOSE_ALL',
      containerName,
    });
  },

  /**
   * 提供事件总线，用于监听模态框事件
   */
  eventBus: modalEventBus,
};

/**
 * Modal容器组件的属性
 */
interface ModalContainerProps {
  name?: string; // 容器名称，默认为 'default'
}

/**
 * Modal容器组件 - 可以挂载多个，通过 name 属性区分
 */
export const ModalContainer: React.FC<ModalContainerProps> = ({ name = 'default' }) => {
  const [modals, setModals] = React.useState<Record<string, React.ReactNode>>({});

  const rejectMapRef = React.useRef<Record<string, () => void>>({});

  React.useEffect(() => {
    // 订阅模态框消息
    const unsubscribe = modalEventBus.subscribe((message: ModalEvent) => {
      if (message.type === 'CLOSE_ALL') {
        if (!message.containerName || message.containerName === name) {
          Object.values(rejectMapRef.current).forEach(reject => reject());
          rejectMapRef.current = {};
          setModals({});
        }
        return;
      }

      if (message.containerName !== name) {
        return;
      }

      const { id, component: ModalComponent, props, resolve, reject } = message;

      // 处理确认
      const handleOk = async (result?: unknown) => {
        // 先执行回调函数，支持异步回调，等待完成后再关闭对话框
        if (typeof props.onOk === 'function') {
          const okResult = props.onOk(result);
          if (okResult && typeof okResult.then === 'function') {
            await okResult;
          }
        }
        if (typeof props.onSuccess === 'function') {
          const successResult = props.onSuccess(result);
          if (successResult && typeof successResult.then === 'function') {
            await successResult;
          }
        }
        if (typeof props.onConfirm === 'function') {
          const confirmResult = props.onConfirm(result);
          if (confirmResult && typeof confirmResult.then === 'function') {
            await confirmResult;
          }
        }

        setModals(prev => {
          const newModals = { ...prev };
          delete newModals[id];
          return newModals;
        });
        delete rejectMapRef.current[id];
        resolve(result);
      };

      const handleCancel = () => {
        setModals(prev => {
          const newModals = { ...prev };
          delete newModals[id];
          return newModals;
        });
        delete rejectMapRef.current[id];

        if (typeof props.onCancel === 'function') {
          props.onCancel();
        }
        reject();
      };

      rejectMapRef.current[id] = handleCancel;

      // 合并属性
      const modalProps: ModalComponentProps = {
        ...props,
        visible: true,
        open: true,
        onOk: handleOk,
        onSuccess: handleOk,
        onConfirm: handleOk,
        onCancel: handleCancel,
      };

      // 添加到状态中
      setModals(prev => ({
        ...prev,
        [id]: <ModalComponent key={id} {...modalProps} />,
      }));
    });

    // 组件卸载时取消订阅
    return unsubscribe;
  }, [name]); // 依赖 name，当 name 变化时重新订阅

  // 使用 createPortal 渲染到 document.body，避免被父级 stacking context 遮挡
  const modalContent = <>{Object.values(modals)}</>;
  return ReactDOM.createPortal(modalContent, document.body);
};

export default ModalService;
